import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../components/Input'
import { Select } from '../components/Select'
import { PATIENT_LANGUAGES, communicationLanguage } from '../data/patientLanguages'
import { FormNavigation } from '../components/FormNavigation'
import { useOnboardingStore } from '../store/onboardingStore'
import { phoneCheckService, vertraegeService } from '../services/api'
import { PtStopScreen } from './psychotherapy/PtStopScreen'
import { useTenant } from '../context/TenantContext'
import { useIsItaly } from './italy/useIsItaly'
import { ItalyPersonalFields, isItalyPersonalDataValid } from './italy/ItalyPersonalFields'
import type { StepProps, Gender } from '../types'
import {
  formatName,
  validateName,
  validateBirthDate,
  getMinBirthDate,
  getMaxBirthDate,
  validatePhoneNumber,
  detectPhoneCountry,
  formatPhoneNumber,
  normalizePhoneNumberOnBlur,
  validateBirthPlace,
  sanitizeBirthPlace, isValidEmail } from '../utils/validation'
import { logger } from '../utils/logger'

const GENDER_VALUES: Gender[] = ['male', 'female', 'diverse']

export function Step2PersonalData({ onNext, onBack }: StepProps) {
  const { t, i18n } = useTranslation()
  const isItaly = useIsItaly()
  const { tenant } = useTenant()
  // Italy asks only Maschile/Femminile — no "Altro" option
  const genderValues = isItaly ? GENDER_VALUES.filter((v) => v !== 'diverse') : GENDER_VALUES
  const {
    personalData,
    setPersonalData,
    patientNumber,
    phoneNumber,
    setPhoneNumber,
    cohort,
    ptStopped,
    setPtStopped,
  } = useOnboardingStore()
  const [stopping, setStopping] = useState(false)

  // Field-level errors
  const [errors, setErrors] = useState<{
    firstName?: string
    lastName?: string
    email?: string
    birthDate?: string
    birthPlace?: string
    phone?: string
  }>({})

  // Cross-check against the number PsyCloud has on file (warn-only). A mistyped
  // number is a silent dead end: the OTP SMS later goes to the wrong handset.
  const [phoneMismatch, setPhoneMismatch] = useState<{ maskedReference?: string } | null>(null)
  // The signing service would refuse the number (invalid) or cannot text it (landline): warn once,
  // the patient corrects it or confirms by pressing Next again (warning is tied to the number)
  const [phoneWarning, setPhoneWarning] = useState<{ kind: 'invalid' | 'landline'; forNumber: string } | null>(null)
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)

  // Validate phone and detect country
  const phoneValidation = useMemo(() => validatePhoneNumber(phoneNumber), [phoneNumber])
  const detectedCountry = useMemo(() => detectPhoneCountry(phoneNumber), [phoneNumber])

  const isValidPhone = phoneValidation.valid

  // Safe access to email (handles undefined from old localStorage data)
  const email = personalData.email || ''

  // Email validation
  const validateEmail = (emailValue: string): string | undefined => {
    if (!emailValue.trim()) return t('step2.emailRequired')
    if (!isValidEmail(emailValue)) return t('step2.emailInvalid')
    return undefined
  }

  // Psychotherapy patients under 18 cannot continue here (guardians sign — later, with the minors
  // flow). Instead of a dead-end validation message they confirm the date, the secretariat is told
  // (vertraege.stopped) and gets in touch.
  const isMinorPt = cohort === 'psychotherapy' && !!personalData.birthDate
    && !validateBirthDate(personalData.birthDate, { minAge: 0 }) && !!validateBirthDate(personalData.birthDate, { minAge: 18 })
  const confirmMinorStop = async () => {
    setStopping(true)
    try {
      await vertraegeService.stop(personalData.birthDate)
    } catch (err) {
      // The stop screen shows either way; the secretariat sees the booking without a completed form
      logger.warn('[Step2] psychotherapy stop not recorded:', err)
    } finally {
      setStopping(false)
      setPtStopped(true)
    }
  }

  // Check all fields are valid
  const isValid =
    personalData.gender &&
    personalData.firstName.trim().length >= 2 &&
    personalData.lastName.trim().length >= 2 &&
    email.trim() &&
    !validateEmail(email) &&
    personalData.birthDate &&
    !validateBirthDate(personalData.birthDate, { minAge: 18 }) &&
    personalData.birthPlace.trim().length >= 2 &&
    (!isItaly || isItalyPersonalDataValid(personalData)) &&
    isValidPhone

  // Auto-format and validate name on blur
  const handleNameBlur = (field: 'firstName' | 'lastName') => {
    const value = personalData[field]
    if (!value) return

    // Auto-format the name
    const formatted = formatName(value)
    if (formatted !== value) {
      setPersonalData({ [field]: formatted })
    }

    // Validate
    const label = field === 'firstName' ? t('step2.firstName') : t('step2.lastName')
    const error = validateName(formatted, label)
    setErrors(prev => ({ ...prev, [field]: error }))
  }

  // Handle birth date change with validation
  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPersonalData({ birthDate: value })

    // Clear error on change
    if (errors.birthDate) {
      setErrors(prev => ({ ...prev, birthDate: undefined }))
    }
  }

  const handleBirthDateBlur = () => {
    if (personalData.birthDate) {
      const error = validateBirthDate(personalData.birthDate, { minAge: 18 })
      setErrors(prev => ({ ...prev, birthDate: error }))
    }
  }

  // Handle birth place input - sanitize on every change
  const handleBirthPlaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeBirthPlace(e.target.value)
    setPersonalData({ birthPlace: sanitized })
    if (errors.birthPlace) setErrors(prev => ({ ...prev, birthPlace: undefined }))
  }

  // Handle birth place with auto-formatting on blur
  const handleBirthPlaceBlur = () => {
    const value = personalData.birthPlace
    if (!value) return

    // Sanitize the city name (already sanitized, but ensure)
    const sanitized = sanitizeBirthPlace(value)
    // Capitalize first letter of each word
    const formatted = sanitized
      .toLowerCase()
      .replace(/(^|[\s\-'])./g, (match) => match.toUpperCase())

    if (formatted !== value) {
      setPersonalData({ birthPlace: formatted })
    }

    const error = validateBirthPlace(formatted)
    setErrors(prev => ({ ...prev, birthPlace: error }))
  }

  // Handle phone number with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhoneNumber(formatted)
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }))
    }
    // An edited number gets checked again on the next submit
    if (phoneMismatch) setPhoneMismatch(null)
    if (phoneWarning) setPhoneWarning(null)
  }

  const handlePhoneBlur = () => {
    if (!phoneNumber) return

    // Normalize on blur: convert national formats (0172..., 0049...) to international (+49...)
    const normalized = normalizePhoneNumberOnBlur(phoneNumber, tenant?.phonePrefix)
    if (normalized !== phoneNumber) {
      setPhoneNumber(normalized)
    }

    // Validate the normalized value
    const validation = validatePhoneNumber(normalized)
    if (!validation.valid) {
      setErrors(prev => ({ ...prev, phone: validation.error }))
    } else {
      setErrors(prev => ({ ...prev, phone: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid || isCheckingPhone) return

    // Would the contract signing accept this number? (P20931/P10184: a number one digit short only
    // failed at the very end.) Shown once per number; pressing Next again continues.
    if (phoneWarning?.forNumber !== phoneNumber) {
      setIsCheckingPhone(true)
      const assessment = await phoneCheckService.validate(phoneNumber)
      setIsCheckingPhone(false)
      if (!assessment.valid || assessment.mobile === false) {
        setPhoneWarning({ kind: assessment.valid ? 'landline' : 'invalid', forNumber: phoneNumber })
        return
      }
    }

    // Warn (once) when the entered number differs from the one on file —
    // the patient can still proceed by submitting again, since the new
    // number may genuinely be more current than our records.
    if (!phoneMismatch) {
      setIsCheckingPhone(true)
      const check = await phoneCheckService.check(patientNumber, phoneNumber)
      setIsCheckingPhone(false)
      if (check.available && check.match === false) {
        logger.log('[Step2] Phone differs from the number on file:', check.maskedReference)
        setPhoneMismatch({ maskedReference: check.maskedReference })
        return
      }
    }

    // Just save data and proceed - Yousign creation moved to Step3
    logger.log('[Step2] Personal data saved, proceeding to next step...')
    onNext()
  }

  const genderLabels: Record<Gender, string> = {
    male: t('step2.male'),
    female: t('step2.female'),
    diverse: t('step2.diverse'),
  }

  if (ptStopped) return <PtStopScreen />

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {t('step2.title')}
        </h2>
        <p className="text-body text-muted-foreground mb-8">
          {t('step2.subtitle')}
        </p>

        <div className="space-y-5">
          <div>
            <label className="block text-small font-medium text-foreground mb-2">
              {t('step2.gender')}
            </label>
            <div className="flex gap-3">
              {genderValues.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPersonalData({ gender: value })}
                  className={`flex-1 py-3 px-4 rounded-card border text-body font-medium transition-colors ${
                    personalData.gender === value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-white text-foreground hover:border-primary/50'
                  }`}
                >
                  {genderLabels[value]}
                </button>
              ))}
            </div>
          </div>

          <Input
            id="firstName"
            type="text"
            label={t('step2.firstName')}
            placeholder={t('step2.firstNamePlaceholder')}
            value={personalData.firstName}
            onChange={(e) => {
              setPersonalData({ firstName: e.target.value })
              if (errors.firstName) setErrors(prev => ({ ...prev, firstName: undefined }))
            }}
            onBlur={() => handleNameBlur('firstName')}
            error={errors.firstName}
            autoComplete="given-name"
          />

          <Input
            id="lastName"
            type="text"
            label={t('step2.lastName')}
            placeholder={t('step2.lastNamePlaceholder')}
            value={personalData.lastName}
            onChange={(e) => {
              setPersonalData({ lastName: e.target.value })
              if (errors.lastName) setErrors(prev => ({ ...prev, lastName: undefined }))
            }}
            onBlur={() => handleNameBlur('lastName')}
            error={errors.lastName}
            autoComplete="family-name"
          />

          <Input
            id="email"
            type="email"
            label={t('step2.email')}
            placeholder={t('step2.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setPersonalData({ email: e.target.value })
              if (errors.email) setErrors(prev => ({ ...prev, email: undefined }))
            }}
            onBlur={() => {
              const error = validateEmail(email)
              setErrors(prev => ({ ...prev, email: error }))
            }}
            error={errors.email}
            autoComplete="email"
          />

          <Input
            id="birthDate"
            type="date"
            label={t('step2.birthDate')}
            value={personalData.birthDate}
            onChange={handleBirthDateChange}
            onBlur={handleBirthDateBlur}
            error={isMinorPt ? undefined : errors.birthDate}
            min={getMinBirthDate()}
            max={getMaxBirthDate()}
            autoComplete="bday"
          />
          {isMinorPt && (
            <div className="rounded-card border border-amber-300 bg-amber-50 p-4 text-small text-amber-900 space-y-3">
              <p>{t('pt.minor.notice')}</p>
              <div className="flex flex-wrap gap-3">
                <button type="button" className="btn-primary" disabled={stopping} onClick={confirmMinorStop}>
                  {stopping ? t('common.loading') : t('pt.minor.confirm')}
                </button>
                <button type="button" className="underline" onClick={() => setPersonalData({ birthDate: '' })}>
                  {t('pt.minor.correct')}
                </button>
              </div>
            </div>
          )}

          <Input
            id="birthPlace"
            type="text"
            label={t('step2.birthPlace')}
            placeholder={t('step2.birthPlacePlaceholder')}
            value={personalData.birthPlace}
            onChange={handleBirthPlaceChange}
            onBlur={handleBirthPlaceBlur}
            error={errors.birthPlace}
          />

          {isItaly && <ItalyPersonalFields />}

          <Input
            id="phone"
            type="tel"
            label={t('step2.phone')}
            placeholder={t('step2.phonePlaceholder')}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            error={errors.phone}
            icon={<span className="text-lg">{detectedCountry?.flag || '🌍'}</span>}
            autoComplete="tel-international"
          />
          {phoneWarning && phoneWarning.forNumber === phoneNumber && (
            <p className="text-small text-amber-700 bg-amber-50 border border-amber-200 rounded-card p-3" role="alert" data-testid="phone-warning" data-kind={phoneWarning.kind}>
              {t(phoneWarning.kind === 'invalid' ? 'step2.phoneLooksInvalid' : 'step2.phoneLandline', { next: t('common.next') })}
            </p>
          )}
          {phoneMismatch && (
            <p className="text-small text-amber-700 bg-amber-50 border border-amber-200 rounded-card p-3">
              {phoneMismatch.maskedReference
                ? t('step2.phoneMismatchWarning', { reference: phoneMismatch.maskedReference })
                : t('step2.phoneMismatchWarningNoRef')}
            </p>
          )}

          <div>
            <Select
              id="communicationLanguage"
              data-testid="communication-language"
              label={t('step2.communicationLanguage')}
              value={communicationLanguage(personalData.communicationLanguage, i18n.language) ?? ''}
              onChange={(e) => {
                const code = PATIENT_LANGUAGES.find(l => l.code === e.target.value)?.code
                if (code) setPersonalData({ communicationLanguage: code })
              }}
              options={PATIENT_LANGUAGES.map(l => ({ value: l.code, label: l.label }))}
            />
            <p className="text-small text-muted-foreground mt-1">{t('step2.communicationLanguageHint')}</p>
          </div>
        </div>

        <FormNavigation onBack={onBack} nextDisabled={!isValid || isCheckingPhone} />
      </form>
    </div>
  )
}
